import React, { useCallback, useMemo, useState } from 'react'
import { AppTitlePage } from '../../components/AppTitlePage'
import { AppBtnColor } from '../../components/btn/AppBtn'
import { AllRoutes } from '../../core/routes/AllRoutes'
import { useNavigate } from 'react-router'
import { AppTable, IHeaderData } from '../../components/AppTable/AppTable'

import {
  useGetBuffetMilletQuery,
  useUpdateShowDefaultMilletMutation,
} from '../../core/api/BuffetMilletApi'
import { AppTableImageViewer } from '../../components/AppTableImageViewer/AppTableImageViewer'
import {
  IBuffetMilletDto,
  IBuffetMilletRequest,
} from '../../core/api/dto/BuffetMilletDto'
import { useParamsControl } from '../../utils/useParamsControl'
import { AppToggle } from 'components/AppToggle'
import { AppLoader, LoaderType } from 'components/AppLoader'
import { AppFilterInput } from '../../components/AppInput/AppFilterInput'
import {
  AppNotification,
  NotificationType,
} from '../../components/notification/Notification'
import { FileUploadMilletPopup } from './components/FileUploadMilletPopup'
import { GetFile } from '../../utils/getFile'
import { createQueryStringSmart } from '../../utils/createQueryString'
import { BASE_URL } from '../../core/api/BaseApi'
import { ImagePreviewWrapperSizeEnum } from 'shared/ui/ImagePreviewWrapper/ImagePreviewWrapper'
import {
  AppTableAlignType,
  AppTableLink,
} from '../../components/AppTable/components/AppTableLink'
import { AppTableNumberBlock } from '../../components/AppTable/components/AppTableNumberBlock'
import { useTranslation } from 'react-i18next'

import { useDeleteMillet } from './hooks/useDeleteMillet'
import { IChooseItems } from 'core/api/dto/BaseDto'
import { AppDeletePopup } from 'entities/AppDeletePopup/AppDeletePopup'

const headerData: IHeaderData[] = [
  { title: 'Наименование', colWidth: '65%', sort: 'name' },
  { title: 'Вес, кг', colWidth: '15%', sort: 'weight', align: 'right' },
  { title: 'Фото', colWidth: '15%', align: 'right' },
]

export const MilletPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [updateShowDefaultMillet] = useUpdateShowDefaultMilletMutation()
  const [isArchivedFilter, setIsArchivedFilter] = useState<boolean>(false)

  const pageParams = useParamsControl<
    IBuffetMilletRequest,
    keyof IBuffetMilletRequest
  >({
    withPagination: true,
    whiteList: ['search', 'is_archived', 'ordering', 'edge_servers'],
  })

  const query_params = useMemo<IBuffetMilletRequest | null>(() => {
    if (!pageParams) return null
    return {
      ...pageParams,
      offset: 0,
      is_archived: isArchivedFilter ? undefined : false,
    }
  }, [pageParams, isArchivedFilter])

  const {
    data: millets,
    isLoading: isLoadingGet,
    isFetching: isFetchingGet,
  } = useGetBuffetMilletQuery(query_params!, {
    skip: !query_params,
    refetchOnMountOrArgChange: true,
  })

  //Чтобы не сбрасывались данные тогла универсальной тары при поиске по тарам
  const { data: allMillets } = useGetBuffetMilletQuery(
    {} as IBuffetMilletRequest,
    {
      refetchOnMountOrArgChange: true,
    },
  )

  const isDefaultFilter = allMillets?.results.some((m) => m.isDefault) ?? false

  const toggleDefaultFilter = () => {
    updateShowDefaultMillet({ is_default: !isDefaultFilter })
  }

  const [chooseItems, setChooseItems] = useState<IChooseItems | null>(null)
  const [uploadFilePopup, setUploadFilePopup] = useState<boolean>(false)
  const [isLoadingLocal, setIsLoadingLocal] = useState<boolean>(false)
  const [tableData, setTableData] = useState<IBuffetMilletDto[]>([])

  const {
    deletePopup,
    closeDeletePopup,
    onDeletePopupHandler,
    onDeleteHandler,
  } = useDeleteMillet(chooseItems, setChooseItems)

  const openUploadFilePopupHandler = useCallback(() => {
    setUploadFilePopup(true)
  }, [])

  const unloadMilletsHandler = useCallback(() => {
    setIsLoadingLocal(true)
    const newPageParams = {
      ...pageParams,
      limit: millets?.count,
    }
    const params = createQueryStringSmart(newPageParams)
    GetFile(`${BASE_URL}/buffet_millet/export_millets_to_file/${params}`)
      .then(() =>
        AppNotification({
          msg: t('Выбранные тары успешно выгружены!'),
          type: NotificationType.success,
        }),
      )
      .finally(() => setIsLoadingLocal(false))
  }, [pageParams, millets?.count])

  const actions = useMemo(
    () => [
      {
        btnTitle: 'Загрузить',
        btnIco: 'import',
        onClick: openUploadFilePopupHandler,
        color: AppBtnColor.whiteOutlineViolet,
      },
      {
        btnTitle: 'Выгрузить',
        btnIco: 'export',
        onClick: unloadMilletsHandler,
        color: AppBtnColor.whiteOutlineViolet,
      },
      {
        btnTitle: 'Добавить тару',
        btnIco: 'add',
        onClick: () => navigate(AllRoutes.addMillet.path),
        color: AppBtnColor.whiteOutlineViolet,
      },
    ],
    [openUploadFilePopupHandler, unloadMilletsHandler, navigate],
  )

  const tableActions = [
    {
      title: 'Удалить',
      ico: 'basket',
      onClick: onDeletePopupHandler,
    },
  ]

  if (!millets && isLoadingGet) {
    return <AppLoader loaderType={LoaderType.main} />
  }

  if (!millets) {
    return null
  }

  const toggleArchivedFilter = () => {
    setIsArchivedFilter((prev) => !prev)
  }

  return (
    <React.Fragment>
      {isLoadingLocal && <AppLoader loaderType={LoaderType.main} />}
      {uploadFilePopup && (
        <FileUploadMilletPopup setActive={setUploadFilePopup} />
      )}
      {deletePopup && (
        <AppDeletePopup onClose={closeDeletePopup} onDelete={onDeleteHandler} />
      )}

      <div className="width-for-page-wrapper">
        <AppTitlePage title="Тары" actions={actions} />
        <div className="col-7-width-from-1000">
          <div className="filter-block no-wrap mt-20">
            <AppFilterInput
              label="Поиск"
              placeholder="Например: тарелка"
              searchParam={'search'}
              ico={'search'}
              fullWidth
            />
          </div>
          <AppTable
            data={millets}
            headerData={headerData}
            idName={'millet'}
            setTableData={setTableData}
            tableDataSelectors={[
              {
                renderItem: (item) => {
                  return (
                    <AppTableLink
                      path={`${item.id}`}
                      deleteItem={item.isArchived}
                    >
                      {item?.name}
                    </AppTableLink>
                  )
                },
              },
              {
                renderItem: (item) => {
                  return (
                    <AppTableLink
                      path={`${item.id}`}
                      deleteItem={item.isArchived}
                      align={AppTableAlignType.right}
                    >
                      <AppTableNumberBlock
                        value={
                          item?.weight
                            .toFixed(3)
                            .toString()
                            .replace(/[.]/g, ',') || ''
                        }
                      />
                    </AppTableLink>
                  )
                },
              },
              {
                renderItem: (item) => (
                  <AppTableImageViewer
                    image={item.imageOnS3 || item.image || null}
                    imageName={item.name}
                    archived={item.isArchived}
                    size={ImagePreviewWrapperSizeEnum.MEDIUM}
                  />
                ),
              },
            ]}
            chooseMode
            setChooseItems={setChooseItems}
            chooseItems={chooseItems}
            chooseActions={tableActions}
            isLoading={isLoadingGet || isFetchingGet}
            extraContent={
              <>
                <div className="millet-toggle-wrap millet-space">
                  <span>{t('Универсальная тара')}</span>
                  <AppToggle
                    value={isDefaultFilter}
                    onChange={toggleDefaultFilter}
                  />
                </div>
                <div className="millet-toggle-wrap">
                  <span>{t('Архивированные тары')}</span>
                  <AppToggle
                    value={isArchivedFilter}
                    onChange={toggleArchivedFilter}
                  />
                </div>
              </>
            }
          />
        </div>
      </div>
    </React.Fragment>
  )
}
